// src/components/events/EventGrid.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';

// 이벤트 상태 계산 (시작 00:00, 종료 23:59:59)
function getEventStatus(startDate, endDate) {
  const now = new Date();
  const sd = new Date(startDate.replace(/\./g, '-') + 'T00:00:00');
  const ed = new Date(endDate.replace(/\./g, '-') + 'T23:59:59');
  if (now < sd) return { label: '진행예정', color: 'bg-purple-600' };
  if (now > ed) return { label: '종료', color: 'bg-gray-600' };
  return { label: '진행중', color: 'bg-green-500' };
}

export default function EventGrid({ limit = 4 }) {
  const [events, setEvents] = useState([]);
  const [bannerUrls, setBannerUrls] = useState({});

  // Firestore에서 events 구독
  useEffect(() => {
    const coll = collection(db, 'events');
    const unsub = onSnapshot(coll, (snap) => {
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  // Storage에서 배너 URL 로드 (캐시 우선)
  useEffect(() => {
    const storage = getStorage();

    events.forEach((evt) => {
      const cacheKey = `eventBanner_${evt.id}`;
      if (bannerUrls[evt.id]) return;

      const cachedUrl = localStorage.getItem(cacheKey);
      if (cachedUrl) {
        setBannerUrls((prev) => ({ ...prev, [evt.id]: cachedUrl }));
        return;
      }

      const path = evt.bannerImagePath || evt.bannerImageUrl || '';
      if (!path) return;

      if (path.startsWith('http://') || path.startsWith('https://')) {
        setBannerUrls((prev) => ({ ...prev, [evt.id]: path }));
        try {
          localStorage.setItem(cacheKey, path);
        } catch {}
        return;
      }

      getDownloadURL(storageRef(storage, path))
        .then((url) => {
          setBannerUrls((prev) => ({ ...prev, [evt.id]: url }));
          try {
            localStorage.setItem(cacheKey, url);
          } catch {}
        })
        .catch(() => {});
    });
  }, [events, bannerUrls]); // bannerUrls 추가

  // 남은 일수 계산 헬퍼
  const parseEnd = (str) => {
    if (typeof str !== 'string') return null;
    const d = new Date(str.replace(/\./g, '-') + 'T23:59:59');
    return isNaN(d.getTime()) ? null : d;
  };
  const today = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;

  return (
    <section className="container mx-auto px-6 pb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {events.slice(0, limit).map((evt) => {
          const { label: statusLabel, color: statusColor } = getEventStatus(
            evt.startDate,
            evt.endDate
          );

          let daysLeft = 0;
          if (statusLabel === '진행중') {
            const ed = parseEnd(evt.endDate);
            daysLeft = ed
              ? Math.floor((ed.getTime() - today.getTime()) / msPerDay)
              : 0;
          }

          let extraBadge = null;
          if (statusLabel === '진행중') {
            if (daysLeft >= 1 && daysLeft <= 3) {
              extraBadge = `D-${daysLeft}`;
            } else if (daysLeft === 0) {
              extraBadge = '마감임박';
            }
          }

          const banner = bannerUrls[evt.id] || '';

          return (
            <Link
              key={evt.id}
              to={`/events/detail/${evt.id}`}
              className="relative group bg-[#1f1f1f] border-2 border-neutral-600
              rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition flex flex-col"
            >
              <div className="absolute top-3 left-3 flex space-x-2 z-10">
                <span
                  className={`px-2 py-1 rounded-full text-sm font-semibold text-white ${statusColor}`}
                >
                  {statusLabel}
                </span>
                {extraBadge && (
                  <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {extraBadge}
                  </span>
                )}
              </div>

              <div className="w-full aspect-banner bg-black overflow-hidden flex items-center justify-center">
                {banner ? (
                  <img
                    src={banner}
                    alt={evt.title}
                    className="object-cover w-full h-full"
                    draggable={false}
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-800">
                    <span className="text-gray-500">이미지 없음</span>
                  </div>
                )}
              </div>

              <div className="p-4 bg-neutral-800 flex flex-col justify-between flex-1">
                <h3 className="text-xl font-bold text-white truncate">
                  {evt.title}
                  {evt.subtitle && (
                    <p className="text-gray-400 text-sm truncate mt-1">
                      {evt.subtitle}
                    </p>
                  )}
                </h3>
                <div className="text-white/80 text-sm flex justify-end items-center mt-2">
                  <span>
                    {evt.startDate} ~ {evt.endDate}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}

        {events.length === 0 && (
          <p className="col-span-full text-center text-gray-500">
            등록된 이벤트가 없습니다.
          </p>
        )}
      </div>
    </section>
  );
}